import { ButtonIcon } from "@fpsoftware/mvp";
import {
  type ArrayPath,
  type Control,
  type FieldArray,
  type FieldPath,
  type FieldValues,
  useFieldArray,
} from "react-hook-form";
import { TbPlus, TbTrash } from "react-icons/tb";
import { Button } from "@/components/Button";
import { NumberInput } from "@/components/form/NumberInput";
import { TextField } from "@/components/form/TextField";
import styles from "./FeeListFields.module.css";

interface FeeListFieldsProps<T extends FieldValues> {
  control: Control<T>;
  /** Path to an array of `{ name: string; amount: number }` on the form. */
  name: ArrayPath<T>;
}

/**
 * Editable list of `{ name, amount }` fees backed by a react-hook-form field
 * array. Shared between the company settings and per-purchase offer management.
 */
export function FeeListFields<T extends FieldValues>({
  control,
  name,
}: FeeListFieldsProps<T>) {
  const { fields, append, remove } = useFieldArray({ control, name });

  return (
    <div className={styles.list}>
      {fields.map((field, index) => (
        <div className={styles.feeRow} key={field.id}>
          <TextField
            label="Name"
            required
            control={control}
            name={`${name}.${index}.name` as FieldPath<T>}
          />
          <NumberInput
            label="Amount"
            required
            control={control}
            name={`${name}.${index}.amount` as FieldPath<T>}
          />
          <ButtonIcon
            icon={TbTrash}
            className={styles.feeDelete}
            onClick={() => remove(index)}
          >
            Delete
          </ButtonIcon>
        </div>
      ))}
      <div>
        <Button
          icon={TbPlus}
          variant="ghost"
          onClick={() =>
            append({ name: "", amount: "" } as FieldArray<T, ArrayPath<T>>)
          }
        >
          Add fee
        </Button>
      </div>
    </div>
  );
}
